'use client'

import * as React from 'react'

/**
 * Keyboard browsing for the results grid (/search).
 *
 * Design note: the grid owns NO per-card state. Every action key maps onto a
 * control that already exists in the focused card's DOM and is driven by a
 * dispatched click, so the auth gate, the liked-list cap toast, the
 * Just-the-facts upgrade prompt and the photo counter/dots all behave exactly
 * as they do for a mouse. That keeps the whole feature additive.
 *
 * Browser defaults are never stolen before the user opts in: ArrowUp/Down,
 * Space and the action keys only bind once browsing is ACTIVE (index != null).
 * The only unprompted bindings are `j`/`k` (bound to nothing else), `?`, and
 * focusing a card with Tab — so plain arrow-key page scrolling keeps working
 * for everyone who never asks for keyboard browsing.
 */

// The header is sticky at h-[90px] (components/layout/header.tsx) — without this
// offset "next" parks the focused card underneath it.
const HEADER_OFFSET = 102

const LIKE = '[data-kbd="like"] button'
const HIDE = '[data-kbd="hide"] button'
const SUMMARY = '[data-kbd="summary"] button'

// PhotoFrame mounts both arrows whenever a listing has >1 photo and labels them
// with stable, unlocalised strings (components/roostr/photo.tsx) — so photo
// stepping needs nothing threaded through the card.
const PHOTO_PREV = 'button[aria-label="Previous photo"]'
const PHOTO_NEXT = 'button[aria-label="Next photo"]'

/** Marks a card as a keyboard stop. Also carries the listing id. */
export const CARD_ATTR = 'data-listing-card'

const HINT_LINGER_MS = 8000

function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null
  if (!node || !node.tagName) return false
  const tag = node.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable
}

export interface GridKeyboardNav {
  /** Index of the focused card, or null when browsing is not active. */
  index: number | null
  /** True while the hint bar should be on screen. */
  hintVisible: boolean
  shortcutsOpen: boolean
  setShortcutsOpen: (open: boolean) => void
  /** tabIndex for card `i` — one tab stop for the whole grid (roving). */
  tabIndexFor: (i: number) => 0 | -1
}

export function useGridKeyboardNav(opts: {
  containerRef: React.RefObject<HTMLElement | null>
  /** Number of rendered cards; used to clamp the index when results change. */
  count: number
  /** True while another surface owns the keyboard (palette, gallery, …). */
  blocked?: boolean
  onOpen: (listingId: string, newTab: boolean) => void
}): GridKeyboardNav {
  const { containerRef, count, blocked = false, onOpen } = opts

  const [index, setIndex] = React.useState<number | null>(null)
  const [hintVisible, setHintVisible] = React.useState(false)
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false)

  // The keydown listener is attached once; current values reach it via a ref.
  const live = React.useRef({ index, blocked, shortcutsOpen, onOpen })
  React.useEffect(() => {
    live.current = { index, blocked, shortcutsOpen, onOpen }
  }, [index, blocked, shortcutsOpen, onOpen])

  const cards = React.useCallback(
    () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>(`[${CARD_ATTR}]`) ?? []),
    [containerRef]
  )

  const hintTimer = React.useRef<number | null>(null)
  const pokeHint = React.useCallback(() => {
    setHintVisible(true)
    if (hintTimer.current) window.clearTimeout(hintTimer.current)
    hintTimer.current = window.setTimeout(() => setHintVisible(false), HINT_LINGER_MS)
  }, [])
  React.useEffect(
    () => () => {
      if (hintTimer.current) window.clearTimeout(hintTimer.current)
    },
    []
  )

  const focusAt = React.useCallback(
    (next: number) => {
      const el = cards()[next]
      if (!el) return
      setIndex(next)
      // Scroll ourselves rather than letting focus() do it — the sticky header
      // would otherwise cover the card we just moved to.
      el.focus({ preventScroll: true })
      const r = el.getBoundingClientRect()
      if (r.top < HEADER_OFFSET || r.bottom > window.innerHeight) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        window.scrollTo({
          top: r.top + window.scrollY - HEADER_OFFSET,
          behavior: reduced ? 'auto' : 'smooth',
        })
      }
    },
    [cards]
  )

  const press = React.useCallback(
    (selector: string) => {
      const i = live.current.index
      if (i == null) return
      cards()[i]?.querySelector<HTMLElement>(selector)?.click()
    },
    [cards]
  )

  // Hiding a card unmounts it; once React has re-rendered, land on whatever now
  // occupies that slot so browsing continues down the list.
  const refocusAt = React.useRef<number | null>(null)
  React.useEffect(() => {
    const at = refocusAt.current
    if (at == null) return
    refocusAt.current = null
    const total = cards().length
    if (!total) {
      setIndex(null)
      return
    }
    focusAt(Math.min(at, total - 1))
  }, [count, cards, focusAt])

  // A new search / sort / page rebuilds the list — drop a now-meaningless index.
  // Progressive rendering only grows `count`, so this never fires mid-batch.
  React.useEffect(() => {
    if (refocusAt.current != null) return
    setIndex((cur) => (cur != null && cur >= count ? null : cur))
  }, [count])

  // Tabbing (or clicking) into a card adopts it as the browsing position.
  // focus doesn't bubble; focusin does.
  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onFocusIn = (e: FocusEvent) => {
      const card = (e.target as HTMLElement | null)?.closest<HTMLElement>(`[${CARD_ATTR}]`)
      if (!card) return
      const i = cards().indexOf(card)
      if (i >= 0) {
        setIndex(i)
        pokeHint()
      }
    }
    el.addEventListener('focusin', onFocusIn)
    return () => el.removeEventListener('focusin', onFocusIn)
  }, [containerRef, cards, pokeHint])

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = live.current
      if (s.blocked) return
      if (isTypingTarget(e.target)) return

      // ⌘/Ctrl+Enter opens in a background tab; every other modifier combo
      // belongs to the browser or the command palette.
      const modified = e.metaKey || e.ctrlKey || e.altKey
      if (modified && !(e.key === 'Enter' && (e.metaKey || e.ctrlKey))) return

      if (e.key === '?') {
        e.preventDefault()
        setShortcutsOpen((v) => !v)
        return
      }

      if (s.shortcutsOpen) {
        if (e.key === 'Escape') {
          e.preventDefault()
          setShortcutsOpen(false)
        }
        return
      }

      const active = s.index != null

      const step = (delta: number) => {
        e.preventDefault()
        const total = cards().length
        if (!total) return
        const next =
          s.index == null
            ? delta > 0
              ? 0
              : total - 1
            : Math.min(total - 1, Math.max(0, s.index + delta))
        focusAt(next)
        pokeHint()
      }

      switch (e.key) {
        // Entry points — `j`/`k` are bound to nothing else, so they may claim
        // the key before browsing is active.
        case 'j':
        case 'J':
          return step(1)
        case 'k':
        case 'K':
          return step(-1)

        // Everything below would trample a browser default, so it waits until
        // the user has actually started browsing.
        case 'ArrowDown':
          if (active) step(1)
          return
        case 'ArrowUp':
          if (active) step(-1)
          return
        case ' ':
          if (active) step(e.shiftKey ? -1 : 1)
          return
        case 'ArrowRight':
          if (active) {
            e.preventDefault()
            press(PHOTO_NEXT)
            pokeHint()
          }
          return
        case 'ArrowLeft':
          if (active) {
            e.preventDefault()
            press(PHOTO_PREV)
            pokeHint()
          }
          return
        case 'a':
        case 'A':
          if (active) {
            e.preventDefault()
            press(SUMMARY)
            pokeHint()
          }
          return
        case 'l':
        case 'L':
          if (active) {
            e.preventDefault()
            press(LIKE)
            pokeHint()
          }
          return
        case 'x':
        case 'X':
          if (active) {
            e.preventDefault()
            // The focused card unmounts, taking DOM focus with it. Hand the
            // position to the re-focus effect below rather than guessing at a
            // frame — React has not re-rendered yet at this point.
            refocusAt.current = s.index as number
            press(HIDE)
          }
          return
        case 'Enter':
          if (active) {
            const id = cards()[s.index as number]?.getAttribute(CARD_ATTR)
            if (id) {
              e.preventDefault()
              s.onOpen(id, e.metaKey || e.ctrlKey)
            }
          }
          return
        case 'Escape':
          if (active) {
            e.preventDefault()
            const el = cards()[s.index as number]
            el?.blur()
            setIndex(null)
            setHintVisible(false)
          }
          return
        default:
          return
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cards, focusAt, press, pokeHint])

  // The AI summary popover closes on outside-MOUSEDOWN, which keyboard browsing
  // never produces — so leaving a card would strand its popover, anchored to a
  // card that has scrolled away. Close it as the position changes.
  const prevIndex = React.useRef<number | null>(null)
  React.useEffect(() => {
    const from = prevIndex.current
    prevIndex.current = index
    if (from == null || from === index) return
    const open = cards()[from]?.querySelector<HTMLElement>(
      '[data-kbd="summary"] button.bg-primary-container'
    )
    open?.click()
  }, [index, cards])

  const tabIndexFor = React.useCallback(
    (i: number): 0 | -1 => ((index == null ? i === 0 : i === index) ? 0 : -1),
    [index]
  )

  return { index, hintVisible, shortcutsOpen, setShortcutsOpen, tabIndexFor }
}
